'use client';

import * as React from 'react';
import {
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    Crown,
    Loader2,
    Lock,
    Search,
    ShieldAlert,
    ShieldCheck,
    UserMinus,
    UserPlus,
    UserSearch,
    X,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { trpc } from '@/lib/api/trpc';
import { normalizeTgdRoles } from '@/lib/auth/tgd-roles';
import { OrgsTab } from '@/components/iam/OrgsTab';
import {
    toIamFriendlyError,
    type IamFriendlyError,
} from '@/lib/iam/friendly-errors';
import { IamErrorAlert } from '@/lib/iam/IamErrorAlert';
import { canSeeIamDirectory } from './access';

/**
 * Unified IAM directory.
 *
 * Replaces the legacy `/admin/staff` (internal-only, since removed)
 * and `/admin/partner/<slug>/team` (per-partner) bounce for "I just
 * want to find a user and edit their role". Everything is filterable
 * in one table backed by the local `users` table mirror — no Clerk
 * pagination at request time.
 *
 * Edits flow through the same use cases the other admin pages use:
 *   - `partners.setUserRoles`         → setInternalUserRoles use case
 *     (used when the target has no partner scope)
 *   - `partners.setTeamMemberRoles`   → setPartnerStaffRoles use case
 *     (used when the target IS scoped to a partner)
 *   - `partners.setUserPartnerScope`  → setUserPartnerScopeUseCase
 *     (only superadmin can re-home a user across partners)
 *
 * The impersonation guard added in 2026-05 lives at the use-case
 * layer, so any edit triggered from this page is automatically
 * refused if the operator is in an impersonated Clerk session.
 */

const URL_KEYS = {
    q: 'q',
    role: 'role',
    scope: 'scope',
} as const;

const SCOPE_ANY = '__any__';
const SCOPE_INTERNAL = '__internal__';
const ROLE_ANY = '__any__';
const PAGE_SIZE = 50;

interface IamUserRow {
    id: string;
    clerkUserId: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    imageUrl: string | null;
    tgdRoles: string[];
    whitelabelOwnership: string | null;
    createdAt: Date | string | null;
}

function fullName(u: IamUserRow): string {
    const n = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return n.length > 0 ? n : '—';
}

function initials(u: IamUserRow): string {
    const f = u.firstName?.[0] ?? '';
    const l = u.lastName?.[0] ?? '';
    if (f || l) return (f + l).toUpperCase();
    return (u.email?.[0] ?? '?').toUpperCase();
}

/**
 * Per-role color tokens for the role chip dot. Each role gets a
 * distinguishable hue so an operator scanning a 1500-row table can
 * spot all `partneradmin` (amber) or `superadmin` (rose) at a
 * glance — far more readable than 6 generic Badge variants.
 *
 * OKLCH chosen for perceptual uniformity (no role looks
 * disproportionately heavier than another at the same lightness)
 * and to play nicely with the theme tokens already in globals.css.
 * Lightness sits in the 0.55–0.70 band so the dots stay legible on
 * both the white surface and the muted hover background.
 */
const ROLE_COLORS: Record<string, { dot: string; text: string }> = {
    superadmin: {
        dot: 'oklch(0.62 0.20 18)', // rose
        text: 'text-rose-700',
    },
    payment_ops: {
        dot: 'oklch(0.55 0.16 250)', // blue — canonical Ops job
        text: 'text-blue-700',
    },
    tagadapaymentAdmin: {
        dot: 'oklch(0.50 0.14 280)', // indigo
        text: 'text-indigo-700',
    },
    accountmanager: {
        dot: 'oklch(0.62 0.17 149)', // primary green
        text: 'text-emerald-700',
    },
    partneradmin: {
        dot: 'oklch(0.70 0.14 80)', // amber
        text: 'text-amber-700',
    },
    partner_ops: {
        dot: 'oklch(0.58 0.14 220)', // cyan — deprecated dual-read
        text: 'text-cyan-700',
    },
    rx_admin: {
        dot: 'oklch(0.60 0.18 330)', // magenta — TagadaRx module admin
        text: 'text-fuchsia-700',
    },
    rx_ops: {
        dot: 'oklch(0.68 0.12 330)', // lighter magenta — TagadaRx desk
        text: 'text-pink-700',
    },
};

const ROLE_FALLBACK = { dot: 'oklch(0.55 0.04 257)', text: 'text-slate-600' };

function roleColor(role: string) {
    return ROLE_COLORS[role] ?? ROLE_FALLBACK;
}

const DEPRECATED_DISPLAY_ROLES = new Set([
    'tagadapaymentAdmin',
    'partner_ops',
    'crmAdmin',
]);

function RoleChip({ role, className }: { role: string; className?: string }) {
    const c = roleColor(role);
    const deprecated = DEPRECATED_DISPLAY_ROLES.has(role);
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 font-mono text-[11px] tracking-tight',
                c.text,
                className,
            )}
            title={
                deprecated
                    ? 'Deprecated — dual-read only. Cannot grant. Prefer payment_ops (+ scope).'
                    : undefined
            }
        >
            <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: c.dot }}
            />
            {role}
            {deprecated ? (
                <span className="text-[9px] uppercase tracking-wide text-muted-foreground">
                    deprecated
                </span>
            ) : null}
        </span>
    );
}

/**
 * Inline stat cell for the page summary strip. NOT the
 * hero-metric-card template — these are tabular numbers separated
 * by hairlines, scannable in one horizontal sweep, no big
 * decorative number, no supporting "% change" line. The optional
 * accent dot ties the number to its role color so the eye links
 * "47 partner-scoped" to the amber chips in the table below.
 */
function Stat({
    label,
    value,
    loading,
    accent,
}: {
    label: string;
    value: number;
    loading?: boolean;
    accent?: string;
}) {
    return (
        <div className="flex items-baseline gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {label}
            </span>
            <span className="flex items-center gap-1.5 text-base font-semibold tabular-nums">
                {accent ? (
                    <span
                        aria-hidden
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: accent }}
                    />
                ) : null}
                {loading ? (
                    <span className="inline-block h-4 w-8 animate-pulse rounded bg-muted" />
                ) : (
                    value.toLocaleString()
                )}
            </span>
        </div>
    );
}

function Divider() {
    return (
        <span aria-hidden className="hidden h-4 w-px bg-border sm:inline-block" />
    );
}

function RowSkeletons() {
    return (
        <div className="overflow-hidden rounded-lg border bg-card">
            <div className="border-b bg-muted/40 px-4 py-2.5">
                <div className="h-3 w-16 rounded bg-muted" />
            </div>
            <div className="divide-y">
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3">
                        <div className="h-9 w-9 animate-pulse rounded-full bg-muted" />
                        <div className="flex-1 space-y-1.5">
                            <div className="h-3 w-32 animate-pulse rounded bg-muted" />
                            <div className="h-2.5 w-48 animate-pulse rounded bg-muted/60" />
                        </div>
                        <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                        <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                    </div>
                ))}
            </div>
        </div>
    );
}

function EmptyState({
    hasFilter,
    onClearFilters,
}: {
    hasFilter: boolean;
    onClearFilters: () => void;
}) {
    return (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <UserSearch className="h-5 w-5" />
            </div>
            <p className="text-sm font-medium">No users match these filters</p>
            <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                {hasFilter
                    ? 'Try widening the search, removing a role filter, or switching to a different partner scope.'
                    : 'The mirror is empty — no Tagada users have synced from Clerk yet.'}
            </p>
            {hasFilter && (
                <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={onClearFilters}
                >
                    Clear filters
                </Button>
            )}
        </div>
    );
}

/**
 * One table row. The whole row is the click target (better than a
 * fiddly per-cell button) and gets a primary-tinted background when
 * the user is currently open in the edit sheet so the operator
 * never loses their place after closing and reopening the sheet.
 */
function UserRow({
    user,
    onClick,
    selected,
}: {
    user: IamUserRow;
    onClick: () => void;
    selected?: boolean;
}) {
    const partnerInitial =
        user.whitelabelOwnership?.[0]?.toUpperCase() ?? '';
    return (
        <tr
            onClick={onClick}
            className={cn(
                'cursor-pointer transition-colors',
                selected ? 'bg-primary/5' : 'hover:bg-muted/40',
            )}
        >
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                        {user.imageUrl ? (
                            <AvatarImage src={user.imageUrl} />
                        ) : null}
                        <AvatarFallback className="bg-muted text-xs font-medium text-foreground">
                            {initials(user)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                        <div className="truncate text-sm font-medium leading-tight">
                            {fullName(user)}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                            {user.email ?? <em>no email</em>}
                        </div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3">
                {user.tgdRoles.length === 0 ? (
                    <span className="text-xs italic text-muted-foreground/70">
                        none
                    </span>
                ) : (
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {/* Canonicalise: legacy lowercase variants
                            (e.g. `tagadapaymentadmin`) collapse to
                            their canonical form so the directory is
                            consistent with the edit sheet. Unknown
                            roles are dropped here too — they'd just
                            confuse the operator without giving them
                            anything to act on. */}
                        {normalizeTgdRoles(user.tgdRoles).map((r) => (
                            <RoleChip key={r} role={r} />
                        ))}
                    </div>
                )}
            </td>
            <td className="px-4 py-3">
                {user.whitelabelOwnership ? (
                    <span className="inline-flex items-center gap-2 text-xs">
                        <span
                            aria-hidden
                            className="flex h-5 w-5 items-center justify-center rounded text-[10px] font-semibold text-amber-900"
                            style={{ background: 'oklch(0.94 0.06 80)' }}
                        >
                            {partnerInitial}
                        </span>
                        <span className="font-mono text-foreground">
                            {user.whitelabelOwnership}
                        </span>
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                        <span
                            aria-hidden
                            className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40"
                        />
                        Internal
                    </span>
                )}
            </td>
            <td className="hidden px-4 py-3 text-xs text-muted-foreground md:table-cell">
                {user.createdAt
                    ? new Date(user.createdAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                      })
                    : '—'}
            </td>
        </tr>
    );
}

function readSearchParams(): URLSearchParams {
    if (typeof window === 'undefined') return new URLSearchParams();
    return new URLSearchParams(window.location.search);
}

function writeSearchParam(key: string, value: string | null) {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (value === null || value === '') {
        url.searchParams.delete(key);
    } else {
        url.searchParams.set(key, value);
    }
    window.history.replaceState({}, '', url.toString());
}

/**
 * Unified IAM directory.
 *
 * Open to the roles the server lets read the directory
 * (`canSeeIamDirectory`, mirror of `canManagePartners('list')`). Per-row
 * mutations are gated by IAM policy at the use-case layer, so the
 * backend will reject a callable a viewer should not be able to run
 * even if they hand-craft a tRPC request. The UI mirrors that policy
 * by hiding (not just disabling) the buttons a viewer cannot use, so
 * the page reads cleanly for non-mutators while staying useful.
 *
 * Capability matrix (mirrors backend):
 *   - superadmin                  → can edit roles + scope of anyone
 *                                   (internal & partner-staff), invite,
 *                                   revoke from any partner
 *   - unscoped payment_ops (or legacy payment_ops / tagadapaymentAdmin)
 *                                 → can manage partner staff (any
 *                                   partner) — invite, revoke, update
 *                                   partner-assignable roles
 *   - partneradmin                → can manage partner staff for their
 *                                   own scope only
 *   - accountmanager              → read-only directory; no actions
 *   - everyone else               → no access, no query fired
 */
export default function AdminIamPage() {
    const claims = useSessionClaims();
    const {
        isSuperAdmin,
        isPartnerAdmin,
        isFounder,
        whitelabelScope,
        tgdRoles,
        isLoaded,
        isSignedIn,
    } = claims;
    const canSeeDirectory = isSignedIn && canSeeIamDirectory(claims);
    const loadingRole = !isLoaded;
    // Anyone holding any tagada role on a partner staff path is allowed to
    // mutate within their scope (the use-case enforces the precise rule).
    // The chrome only needs a coarse "can this user mutate anything"
    // signal to decide whether to render the Invite button + edit sheet.
    // Scoped payment_ops is partner-book Ops only — not a platform assigner.
    const isHubAdmin =
        !whitelabelScope &&
        (tgdRoles.includes('payment_ops') ||
            tgdRoles.includes('payment_ops') ||
            tgdRoles.includes('tagadapaymentAdmin'));
    const canManageAnyPartnerStaff =
        isSuperAdmin || isHubAdmin || isPartnerAdmin;
    const canMutate = isSuperAdmin || canManageAnyPartnerStaff;

    // Read initial filter state from the URL so deep-links stay shareable
    // (a colleague pings you "this user has weird roles, look at /admin/iam?q=jarred").
    const initial = React.useMemo(() => readSearchParams(), []);
    const [q, setQ] = React.useState<string>(initial.get(URL_KEYS.q) ?? '');
    const [role, setRole] = React.useState<string>(
        initial.get(URL_KEYS.role) ?? ROLE_ANY,
    );
    const [scope, setScope] = React.useState<string>(
        initial.get(URL_KEYS.scope) ?? SCOPE_ANY,
    );
    const [page, setPage] = React.useState<number>(0);
    // Debounce the search input so we don't fire a new query on every
    // keystroke. 250ms feels instantaneous and keeps the Postgres ILIKE
    // running on staff-scale data without a tsvector.
    const [qDebounced, setQDebounced] = React.useState<string>(q);
    React.useEffect(() => {
        const t = setTimeout(() => setQDebounced(q), 250);
        return () => clearTimeout(t);
    }, [q]);

    // Sync URL on filter change (after debounce for q so we don't thrash
    // history). Page is intentionally NOT in the URL — it resets on any
    // filter change because the current page might not exist in the new
    // filter's result set.
    React.useEffect(() => {
        writeSearchParam(URL_KEYS.q, qDebounced || null);
    }, [qDebounced]);
    React.useEffect(() => {
        writeSearchParam(URL_KEYS.role, role === ROLE_ANY ? null : role);
    }, [role]);
    React.useEffect(() => {
        writeSearchParam(URL_KEYS.scope, scope === SCOPE_ANY ? null : scope);
    }, [scope]);
    React.useEffect(() => {
        setPage(0);
    }, [qDebounced, role, scope]);

    const partnersQuery = trpc.partners.listActivePartnerSlugs.useQuery(
        undefined,
        { enabled: canSeeDirectory, staleTime: 5 * 60 * 1000 },
    );
    const validRolesQuery = trpc.partners.listInternalAssignableRoles.useQuery(
        undefined,
        { enabled: canSeeDirectory, staleTime: 60 * 60 * 1000 },
    );

    const usersQuery = trpc.partners.listIamUsers.useQuery(
        {
            q: qDebounced || undefined,
            tgdRole: role === ROLE_ANY ? undefined : role,
            partnerScope: scope === SCOPE_ANY ? undefined : scope,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
        },
        { enabled: canSeeDirectory, staleTime: 15_000 },
    );

    const [selected, setSelected] = React.useState<IamUserRow | null>(null);
    const [inviteOpen, setInviteOpen] = React.useState(false);

    // Stats are summed over the CURRENT FILTERED PAGE, not the full
    // mirror, because the listIamUsers procedure already paginates
    // and a global tally would be a second query for marginal value.
    // The header label makes the scope explicit so it doesn't read
    // as a misleading global metric.
    //
    // Computed BEFORE any early-return so the hook order stays
    // stable across render passes (otherwise React rules-of-hooks
    // breaks when superadmin auth flips).
    const rowsForStats = (usersQuery.data?.rows ?? []) as IamUserRow[];
    const pageStats = React.useMemo(() => {
        let internal = 0;
        let scoped = 0;
        let superadmins = 0;
        for (const u of rowsForStats) {
            if (u.whitelabelOwnership) scoped++;
            else internal++;
            if (u.tgdRoles.includes('superadmin')) superadmins++;
        }
        return { internal, scoped, superadmins };
    }, [rowsForStats]);

    if (loadingRole) {
        return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
    }

    if (!isSignedIn) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="text-center">
                    <ShieldAlert className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h2 className="mt-4 text-xl font-semibold">Sign in required</h2>
                    <p className="mt-2 max-w-md text-sm text-muted-foreground">
                        Sign in to browse the IAM directory.
                    </p>
                </div>
            </div>
        );
    }

    // Same rule as the server: the queries above are not fired either.
    if (!canSeeDirectory) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="text-center">
                    <ShieldAlert className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h2 className="mt-4 text-xl font-semibold">
                        IAM access reserved to Tagada staff
                    </h2>
                    <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                        This page is only accessible to superadmin, founder,
                        payment_ops, accountmanager, partneradmin or
                        tagadapaymentAdmin users.
                        Ask a platform admin to grant you a role if you need
                        directory access.
                    </p>
                </div>
            </div>
        );
    }

    const partners = partnersQuery.data ?? [];
    const validRoles = validRolesQuery.data?.validRoles ?? [];
    const internalAssignableRoles =
        validRolesQuery.data?.assignableRoles ?? [];
    const partnerAssignableRoles =
        validRolesQuery.data?.partnerAssignableRoles ?? [];
    const rows = rowsForStats;
    const total = usersQuery.data?.total ?? 0;

    const hasFilter = !!qDebounced || role !== ROLE_ANY || scope !== SCOPE_ANY;
    const clearFilters = () => {
        setQ('');
        setRole(ROLE_ANY);
        setScope(SCOPE_ANY);
    };

    // Coarse access label surfaced next to the page title — picks the
    // most-privileged role the caller carries so they immediately see
    // what they can do here without having to inspect their session
    // claims. Read-only viewers explicitly read "viewer" so they're
    // not surprised by missing buttons.
    const accessLabel = isSuperAdmin
        ? 'superadmin'
        : isHubAdmin
            ? 'payment_ops'
            : isPartnerAdmin
                ? `partneradmin · ${whitelabelScope ?? '—'}`
                : 'viewer';

    // Per-row mutation permission. Mirrors backend
    // `canManagePartnerStaff` + the superadmin-only internal-staff
    // mutations (`setInternalUserRoles`, `setUserPartnerScope`).
    // Surfacing this in the sheet header lets read-only viewers see
    // the user's profile without firing 403s on save.
    const canMutateUser = (u: IamUserRow): boolean => {
        if (isSuperAdmin) return true;
        if (!u.whitelabelOwnership) return false; // internal staff: superadmin-only
        if (isHubAdmin) return true; // payment_ops can manage any partner staff
        if (isPartnerAdmin && whitelabelScope === u.whitelabelOwnership) return true;
        return false;
    };

    return (
        <div className="space-y-6">
            <header className="flex flex-wrap items-end justify-between gap-4 border-b pb-4">
                <div>
                    <div className="flex items-baseline gap-2">
                        <h1 className="text-2xl font-semibold tracking-tight">
                            Identity & access
                        </h1>
                        <span className="font-mono text-xs text-muted-foreground">
                            {accessLabel}
                        </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage Tagada users (internal & partner staff) and the
                        merchant organisations they belong to. Replaces the
                        Clerk dashboard for everything role-related — no one
                        should need Clerk dashboard access for IAM anymore.
                    </p>
                </div>
                {canMutate ? (
                    <Button
                        onClick={() => setInviteOpen(true)}
                        className="shrink-0"
                    >
                        <UserPlus className="mr-2 h-4 w-4" /> Invite user
                    </Button>
                ) : null}
            </header>

            <Tabs defaultValue="users" className="space-y-6">
                <TabsList>
                    <TabsTrigger value="users">Users</TabsTrigger>
                    <TabsTrigger value="orgs">Organisations</TabsTrigger>
                </TabsList>

                <TabsContent value="users" className="space-y-6">
            <section
                aria-label="Page summary"
                className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm"
            >
                <Stat label="Matches" value={total} loading={usersQuery.isLoading} />
                <Divider />
                <Stat
                    label="On this page"
                    value={rows.length}
                    loading={usersQuery.isLoading}
                />
                <Divider />
                <Stat
                    label="Internal"
                    value={pageStats.internal}
                    loading={usersQuery.isLoading}
                    accent="oklch(0.62 0.17 149)"
                />
                <Divider />
                <Stat
                    label="Partner-scoped"
                    value={pageStats.scoped}
                    loading={usersQuery.isLoading}
                    accent="oklch(0.70 0.14 80)"
                />
                <Divider />
                <Stat
                    label="Superadmins"
                    value={pageStats.superadmins}
                    loading={usersQuery.isLoading}
                    accent="oklch(0.62 0.20 18)"
                />
            </section>

            <section
                aria-label="Filters"
                className="flex flex-wrap items-center gap-2"
            >
                <div className="relative min-w-0 flex-1 sm:max-w-md">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search email or name…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="h-9 pl-9 pr-8"
                    />
                    {q && (
                        <button
                            type="button"
                            onClick={() => setQ('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label="Clear search"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="h-9 w-auto min-w-[140px]">
                        <SelectValue placeholder="Any role" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ROLE_ANY}>Any role</SelectItem>
                        {validRoles.map((r) => (
                            <SelectItem key={r} value={r}>
                                <RoleChip role={r} />
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {/*
                  * Scope filter. For scoped actors (partneradmin or
                  * scoped accountmanager) the backend silently
                  * overrides whatever they pick — they only ever see
                  * their own partner's users (`listUsers` clamp). We
                  * USED to render the picker anyway, which surfaced
                  * "I picked Internal Tagada and got 0 results" as a
                  * mysterious empty state. Now we replace the picker
                  * with a read-only chip explaining what the user is
                  * actually seeing — same trick `OrgsTab` already
                  * uses, applied here for symmetry.
                  */}
                {whitelabelScope ? (
                    <div
                        className="flex h-9 items-center gap-1.5 rounded-md border border-dashed bg-muted/30 px-3 text-xs text-muted-foreground"
                        title={`Your account is scoped to '${whitelabelScope}'. The directory only shows users attached to this partner.`}
                    >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        <span className="font-medium text-foreground">
                            {whitelabelScope}
                        </span>
                        <span>(your scope)</span>
                    </div>
                ) : (
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger className="h-9 w-auto min-w-[180px]">
                            <SelectValue placeholder="Any scope" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={SCOPE_ANY}>Any scope</SelectItem>
                            <SelectItem value={SCOPE_INTERNAL}>
                                Internal Tagada
                            </SelectItem>
                            {partners.map((p: { slug: string; name: string }) => (
                                <SelectItem key={p.slug} value={p.slug}>
                                    {p.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                {hasFilter && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 text-muted-foreground hover:text-foreground"
                        onClick={clearFilters}
                    >
                        <X className="mr-1.5 h-3.5 w-3.5" /> Clear
                    </Button>
                )}
            </section>

            <section aria-label="Users">
                {usersQuery.isLoading ? (
                    <RowSkeletons />
                ) : usersQuery.error ? (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Failed to load users</AlertTitle>
                        <AlertDescription>
                            {usersQuery.error.message}
                        </AlertDescription>
                    </Alert>
                ) : rows.length === 0 ? (
                    <EmptyState
                        hasFilter={hasFilter}
                        onClearFilters={clearFilters}
                    />
                ) : (
                    <>
                        <div className="overflow-hidden rounded-lg border bg-card">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/40 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                        <th className="px-4 py-2.5 text-left">User</th>
                                        <th className="px-4 py-2.5 text-left">Roles</th>
                                        <th className="px-4 py-2.5 text-left">Scope</th>
                                        <th className="hidden px-4 py-2.5 text-left md:table-cell">
                                            Joined
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {rows.map((u) => (
                                        <UserRow
                                            key={u.clerkUserId}
                                            user={u}
                                            onClick={() => setSelected(u)}
                                            selected={
                                                selected?.clerkUserId ===
                                                u.clerkUserId
                                            }
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                            <div>
                                <span className="font-medium text-foreground">
                                    {page * PAGE_SIZE + 1}
                                </span>
                                <span className="mx-1">–</span>
                                <span className="font-medium text-foreground">
                                    {Math.min(total, (page + 1) * PAGE_SIZE)}
                                </span>
                                <span className="mx-1">of</span>
                                <span className="font-medium text-foreground">
                                    {total.toLocaleString()}
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    disabled={page === 0 || usersQuery.isFetching}
                                    onClick={() =>
                                        setPage((p) => Math.max(0, p - 1))
                                    }
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    disabled={
                                        (page + 1) * PAGE_SIZE >= total ||
                                        usersQuery.isFetching
                                    }
                                    onClick={() => setPage((p) => p + 1)}
                                    aria-label="Next page"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </section>

            <UserEditSheet
                user={selected}
                open={selected !== null}
                onOpenChange={(o) => !o && setSelected(null)}
                internalAssignableRoles={internalAssignableRoles}
                partnerAssignableRoles={partnerAssignableRoles}
                partners={partners}
                onSaved={() => usersQuery.refetch()}
                canMutate={selected ? canMutateUser(selected) : false}
                isFounder={isFounder}
            />

            <InviteDialog
                open={inviteOpen}
                onOpenChange={setInviteOpen}
                partners={partners}
                internalAssignableRoles={internalAssignableRoles}
                partnerAssignableRoles={partnerAssignableRoles}
                onInvited={() => usersQuery.refetch()}
                isSuperAdmin={isSuperAdmin}
                isFounder={isFounder}
                callerPartnerScope={whitelabelScope}
            />
                </TabsContent>

                <TabsContent value="orgs" className="space-y-6">
                    <OrgsTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}

/**
 * Edit sheet for a single user. Surfaces:
 *   - tgdRoles checkboxes (full VALID_TGD_ROLES set; warning on superadmin)
 *   - partner scope dropdown (any active partner, or detach)
 *
 * Saves via the appropriate mutation depending on current scope:
 *   - target scope = null → `setUserRoles` (internal-staff path)
 *   - target scope ≠ null → `setTeamMemberRoles` (partner-staff path,
 *     superadmin-overridden)
 *
 * Scope changes always go through `setUserPartnerScope` (superadmin only,
 * enforced server-side).
 */
/**
 * Roles that ONLY a founder can grant. Mirrors the backend's
 * `FOUNDER_ONLY_GRANTABLE_ROLES` — keep them in lockstep, the UI is
 * the cosmetic layer (greys out the checkbox), the policy in
 * `canAssignTgdRole` is the actual enforcement.
 */
const FOUNDER_ONLY_GRANTABLE_ROLES: ReadonlySet<string> = new Set([
    'superadmin',
    'founder',
]);

function UserEditSheet({
    user,
    open,
    onOpenChange,
    internalAssignableRoles,
    partnerAssignableRoles,
    partners,
    onSaved,
    canMutate,
    isFounder,
}: {
    user: IamUserRow | null;
    open: boolean;
    onOpenChange: (o: boolean) => void;
    internalAssignableRoles: readonly string[];
    partnerAssignableRoles: readonly string[];
    partners: readonly { slug: string; name: string }[];
    onSaved: () => void;
    /**
     * False = caller can browse this user's profile but every input is
     * read-only and the Save / Revoke buttons are hidden. Computed in
     * the parent so we don't recompute role permissions here.
     */
    canMutate: boolean;
    /**
     * True when the caller carries the `founder` tgdRole. Founder-only
     * grants (`superadmin`, `founder`) are gated to this flag; for
     * everyone else those checkboxes are visibly disabled with a
     * "founder only" annotation. Backend `canAssignTgdRole` enforces
     * the same rule — UI gating is just to avoid handing operators a
     * button that 403s.
     */
    isFounder: boolean;
}) {
    const [draftRoles, setDraftRoles] = React.useState<string[]>([]);
    const [draftScope, setDraftScope] = React.useState<string | null>(null);
    /**
     * Persistent error banner for the sheet. We keep this in state
     * (rather than relying on react-hot-toast) because IAM denials
     * need to stay visible until the operator reads them — a 4-second
     * toast for a permission failure on a privileged action is the
     * worst possible UX. Cleared on every fresh save attempt and on
     * sheet close. Mapped via `toIamFriendlyError` so we use the
     * machine-readable backend `reason` (propagated through tRPC
     * `error.data.iamReason`), not the raw developer message.
     */
    const [lastError, setLastError] = React.useState<IamFriendlyError | null>(null);

    React.useEffect(() => {
        if (user) {
            setLastError(null);
            // Canonicalise + dedupe upfront so legacy lowercase variants
            // (e.g. `tagadapaymentadmin` from prod metadata drift) collapse
            // to canonical (`tagadapaymentAdmin`) before the operator
            // touches anything. Without this, the same role would surface
            // twice in the checkbox list — once as "assignable", once as
            // "legacy" — and ticking either would submit BOTH and bounce
            // off the backend Zod schema. Unknown role strings (legacy
            // `sales`, `partner`, …) are silently dropped here; they
            // weren't doing anything anyway and the backend would refuse
            // to round-trip them.
            setDraftRoles(normalizeTgdRoles(user.tgdRoles));
            setDraftScope(user.whitelabelOwnership);
        }
    }, [user]);

    const utils = trpc.useUtils();
    const setRolesInternal = trpc.partners.setUserRoles.useMutation();
    const setRolesTeam = trpc.partners.setTeamMemberRoles.useMutation();
    const setScope = trpc.partners.setUserPartnerScope.useMutation();
    const revokeTeam = trpc.partners.revokeTeamMember.useMutation();

    // Scope-aware checkbox list — surfaces only the roles the backend
    // would actually accept for the user's CURRENT (or about-to-be) scope.
    // Switching the scope dropdown re-keys this list automatically because
    // we read `draftScope` here. Roles already on the user that are NOT in
    // the assignable list (e.g. a legacy `payment_ops` on a partner-scoped
    // account that pre-dates the role tightening) are surfaced too with
    // a warning so the operator can see them — and uncheck them — but
    // they cannot be re-added once removed.
    //
    // NOTE: hooks must run unconditionally — keep this block ABOVE any
    // early return (`if (!user) return null;`).
    const assignableRoles = draftScope
        ? partnerAssignableRoles
        : internalAssignableRoles;
    // `legacyRoles` is what we surface as "non-removable, non-re-addable
    // roles already on the user". Two reasons it needs canonicalisation:
    //   1. dedupe against `assignableRoles` case-insensitively so a
    //      legacy `tagadapaymentadmin` doesn't render alongside the
    //      canonical `tagadapaymentAdmin` checkbox (the bug that put
    //      both into the submit payload and 400'd the IAM panel).
    //   2. drop unknown/silent roles (`sales`, `partner`, prehistoric
    //      tags) — there's no UI affordance that would let the operator
    //      reason about them and the backend would refuse to round-trip
    //      them anyway.
    const canonicalUserRoles = React.useMemo(
        () => normalizeTgdRoles(user?.tgdRoles ?? []),
        [user?.tgdRoles],
    );
    const assignableLowercased = React.useMemo(
        () => new Set(assignableRoles.map((r) => r.toLowerCase())),
        [assignableRoles],
    );

    if (!user) return null;

    const legacyRoles = canonicalUserRoles.filter(
        (r) => !assignableLowercased.has(r.toLowerCase()),
    );
    const visibleRoles = [...assignableRoles, ...legacyRoles];

    // Compare against the CANONICALISED user.tgdRoles, otherwise any
    // user with a legacy lowercase variant would always look "dirty"
    // (because draftRoles was canonicalised at init and user.tgdRoles
    // still has `tagadapaymentadmin` instead of `tagadapaymentAdmin`)
    // and the Save button would be enabled with nothing actually
    // changed. The backend WILL canonicalise on save anyway, so a user
    // who hits Save without touching anything still gets a self-heal
    // pass — but the button correctly stays disabled until the
    // operator does something meaningful.
    const canonicalUserRoleStrings: readonly string[] = canonicalUserRoles;
    const rolesChanged =
        draftRoles.length !== canonicalUserRoleStrings.length ||
        draftRoles.some((r) => !canonicalUserRoleStrings.includes(r));
    const scopeChanged = draftScope !== user.whitelabelOwnership;
    const isSaving =
        setRolesInternal.isPending ||
        setRolesTeam.isPending ||
        setScope.isPending ||
        revokeTeam.isPending;

    const toggleRole = (r: string) => {
        // Block re-adding a legacy role that is no longer assignable in
        // the current scope — once it's off, only the dedicated internal
        // surface can grant it back. This matches what the backend would
        // do anyway (refuse the assignment), but fails fast in the UI.
        if (!assignableRoles.includes(r) && !draftRoles.includes(r)) {
            setLastError({
                title: 'Role not assignable here',
                description: `'${r}' isn't valid in the current scope (${
                    draftScope ? `partner: ${draftScope}` : 'internal staff'
                }). Switching scope re-keys the assignable list.`,
                suggestion: draftScope
                    ? 'Detach the user from the partner first, then re-grant from internal scope.'
                    : null,
                variant: 'warning',
            });
            return;
        }
        // Founder gate (mirrors backend `canAssignTgdRole`). A non-
        // founder cannot tick `superadmin` or `founder`. Already-set
        // values can be UNticked freely (revocation is allowed by the
        // policy too — only the GRANT side is gated).
        if (
            FOUNDER_ONLY_GRANTABLE_ROLES.has(r) &&
            !isFounder &&
            !draftRoles.includes(r)
        ) {
            // Synthesise the same friendly content the backend would
            // surface so the operator sees identical copy regardless
            // of whether the gate fires client-side (here) or server-
            // side (after a stale-page bypass attempt).
            setLastError(
                toIamFriendlyError({
                    name: 'IamAuthorizationError',
                    reason: 'only_founder_can_grant_privileged_role',
                    message: `Roles ['${r}'] can only be granted by a founder.`,
                } as unknown),
            );
            return;
        }
        setLastError(null);
        setDraftRoles((prev) =>
            prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r],
        );
    };

    const onRevoke = async () => {
        if (!user.whitelabelOwnership) {
            setLastError(
                toIamFriendlyError(new Error('User has no partner scope to revoke.')),
            );
            return;
        }
        if (
            !window.confirm(
                `Revoke ${user.email ?? user.clerkUserId} from ${user.whitelabelOwnership}? This strips their roles + scope but keeps the audit row.`,
            )
        ) {
            return;
        }
        try {
            setLastError(null);
            await revokeTeam.mutateAsync({
                partner: user.whitelabelOwnership,
                clerkUserId: user.clerkUserId,
            });
            toast.success(`Revoked ${user.email ?? user.clerkUserId}`);
            await utils.partners.listIamUsers.invalidate();
            onSaved();
            onOpenChange(false);
        } catch (err) {
            // Persistent banner instead of toast: a permission denial
            // here is the operator's most useful piece of information,
            // we should NOT make it disappear in 4 seconds.
            setLastError(toIamFriendlyError(err));
        }
    };

    const onSave = async () => {
        try {
            setLastError(null);
            // Order matters: change scope first if needed, then roles.
            // The reverse can fail because some role/scope combos are
            // policy-rejected (e.g. partneradmin without scope).
            if (scopeChanged) {
                await setScope.mutateAsync({
                    clerkUserId: user.clerkUserId,
                    partnerScope: draftScope,
                });
            }
            if (rolesChanged) {
                // Pick the right write surface based on the user's NEW scope.
                // If we just attached them to a partner, `setTeamMemberRoles`
                // is the correct path (partner-assignable role validation).
                // If they're now unscoped, `setUserRoles` (internal staff).
                const newScope = scopeChanged ? draftScope : user.whitelabelOwnership;
                if (newScope) {
                    await setRolesTeam.mutateAsync({
                        partner: newScope,
                        clerkUserId: user.clerkUserId,
                        roles: draftRoles,
                    });
                } else {
                    await setRolesInternal.mutateAsync({
                        clerkUserId: user.clerkUserId,
                        roles: draftRoles,
                    });
                }
            }
            toast.success(`Updated ${user.email ?? user.clerkUserId}`);
            await utils.partners.listIamUsers.invalidate();
            onSaved();
            onOpenChange(false);
        } catch (err) {
            setLastError(toIamFriendlyError(err));
        }
    };

    // Compare against canonical so a user with legacy `superAdmin`
    // (case drift) doesn't trigger the warning when nothing is being
    // newly granted.
    const wantsSuperadmin =
        draftRoles.includes('superadmin') &&
        !canonicalUserRoleStrings.includes('superadmin');
    const wantsFounder =
        draftRoles.includes('founder') &&
        !canonicalUserRoleStrings.includes('founder');

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
                <SheetHeader className="border-b px-6 pb-5 pt-6">
                    <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12">
                            {user.imageUrl ? (
                                <AvatarImage src={user.imageUrl} />
                            ) : null}
                            <AvatarFallback className="text-base font-medium">
                                {initials(user)}
                            </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                            <SheetTitle className="truncate text-lg leading-tight">
                                {fullName(user)}
                            </SheetTitle>
                            <SheetDescription className="mt-0.5 truncate text-xs">
                                {user.email ?? <em>no email</em>}
                            </SheetDescription>
                            <div className="mt-1 truncate font-mono text-[10px] text-muted-foreground/70">
                                {user.clerkUserId}
                            </div>
                        </div>
                    </div>
                    {canonicalUserRoles.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1.5">
                            {canonicalUserRoles.map((r) => (
                                <RoleChip key={r} role={r} />
                            ))}
                        </div>
                    )}
                </SheetHeader>

                <div className="flex-1 space-y-7 overflow-y-auto px-6 py-6">
                    <section className="space-y-2.5">
                        <div className="flex items-baseline justify-between">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Partner scope
                            </Label>
                            <span className="text-[10px] text-muted-foreground/70">
                                superadmin only
                            </span>
                        </div>
                        <Select
                            value={draftScope ?? SCOPE_INTERNAL}
                            onValueChange={(v) =>
                                setDraftScope(v === SCOPE_INTERNAL ? null : v)
                            }
                            disabled={!canMutate}
                        >
                            <SelectTrigger className="h-10">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={SCOPE_INTERNAL}>
                                    Internal Tagada (detach)
                                </SelectItem>
                                {partners.map((p) => (
                                    <SelectItem key={p.slug} value={p.slug}>
                                        {p.name}{' '}
                                        <span className="text-muted-foreground">
                                            ({p.slug})
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                            {canMutate
                                ? "Re-homing a user clears any role that doesn't apply in the new scope. Done before the role write."
                                : 'Read-only — only a superadmin can change this user\u2019s scope.'}
                        </p>
                    </section>

                    <section className="space-y-2.5">
                        <div className="flex items-baseline justify-between">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Roles
                            </Label>
                            <span className="text-[10px] text-muted-foreground/70">
                                {draftScope
                                    ? `partner-assignable · ${draftScope}`
                                    : 'internal-assignable'}
                            </span>
                        </div>
                        {/*
                          * Caller-capability banner. The operator's first
                          * question is always "what am *I* allowed to do
                          * here?". We surface a one-liner derived from
                          * their tgdRoles so they don't have to read the
                          * documentation, the policy file, or the locked-
                          * checkbox tooltips one by one. Intentionally
                          * compact (single Info chip + 1-line copy) so it
                          * doesn't compete with the actual Save action.
                          */}
                        {canMutate && (
                            <div className="flex items-start gap-2 rounded-md border border-sky-200/60 bg-sky-50/60 px-3 py-2 text-xs leading-relaxed text-sky-900">
                                {isFounder ? (
                                    <Crown className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-rose-600" />
                                ) : (
                                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-sky-700" />
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium">
                                        You can grant{' '}
                                        {assignableRoles.length === 0
                                            ? 'no role here'
                                            : assignableRoles
                                                .filter(
                                                    (r) =>
                                                        isFounder ||
                                                        !FOUNDER_ONLY_GRANTABLE_ROLES.has(
                                                            r,
                                                        ),
                                                )
                                                .join(', ') || 'no role here'}
                                        {!isFounder &&
                                            assignableRoles.some((r) =>
                                                FOUNDER_ONLY_GRANTABLE_ROLES.has(r),
                                            ) && (
                                                <>
                                                    {' '}
                                                    <span className="text-sky-800/70">
                                                        ·{' '}
                                                        <Lock className="mr-0.5 inline h-3 w-3" />
                                                        founder-only:{' '}
                                                        {assignableRoles
                                                            .filter((r) =>
                                                                FOUNDER_ONLY_GRANTABLE_ROLES.has(
                                                                    r,
                                                                ),
                                                            )
                                                            .join(', ')}
                                                    </span>
                                                </>
                                            )}
                                    </div>
                                    <div className="mt-0.5 text-[11px] text-sky-800/80">
                                        Revoking an existing role works even
                                        if you can&apos;t re-grant it.
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="divide-y rounded-md border">
                            {visibleRoles.map((r) => {
                                const isLegacy = !assignableRoles.includes(r);
                                const checked = draftRoles.includes(r);
                                const isFounderOnly =
                                    FOUNDER_ONLY_GRANTABLE_ROLES.has(r);
                                // Only the GRANT side is gated; a non-
                                // founder may still uncheck a previously
                                // granted superadmin/founder. This matches
                                // the backend policy and lets a payment_ops
                                // demote a compromised account without
                                // waiting for a founder.
                                const isFounderLockedForGrant =
                                    isFounderOnly && !isFounder && !checked;
                                const isDisabled =
                                    !canMutate ||
                                    (isLegacy && !checked) ||
                                    isFounderLockedForGrant;
                                return (
                                    <label
                                        key={r}
                                        htmlFor={`role-${r}`}
                                        className={cn(
                                            'flex items-center gap-3 px-3 py-2.5 transition-colors',
                                            isDisabled
                                                ? 'cursor-default'
                                                : 'cursor-pointer',
                                            checked
                                                ? 'bg-muted/50'
                                                : !isDisabled
                                                    ? 'hover:bg-muted/30'
                                                    : '',
                                            isFounderLockedForGrant &&
                                                'bg-muted/20 opacity-70',
                                        )}
                                        title={
                                            isFounderLockedForGrant
                                                ? 'Only a founder can grant this role. Existing grants can still be removed.'
                                                : isLegacy && !checked
                                                    ? 'Legacy role — kept for users that already have it; not re-grantable from this scope.'
                                                    : undefined
                                        }
                                    >
                                        <Checkbox
                                            id={`role-${r}`}
                                            checked={checked}
                                            onCheckedChange={() => toggleRole(r)}
                                            disabled={isDisabled}
                                        />
                                        <RoleChip
                                            role={r}
                                            className="text-[12px]"
                                        />
                                        {isLegacy && !isFounderOnly && (
                                            <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
                                                legacy
                                            </span>
                                        )}
                                        {isFounderOnly && (
                                            <span
                                                className={cn(
                                                    'ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider',
                                                    isFounder
                                                        ? 'text-rose-600'
                                                        : 'text-muted-foreground',
                                                )}
                                            >
                                                {isFounder ? (
                                                    <>
                                                        <Crown className="h-3 w-3" />
                                                        founder grant
                                                    </>
                                                ) : (
                                                    <>
                                                        <Lock className="h-3 w-3" />
                                                        founder only
                                                    </>
                                                )}
                                            </span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                        {wantsSuperadmin && (
                            <Alert
                                variant="destructive"
                                className="mt-3 border-rose-200 bg-rose-50 text-rose-900"
                            >
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle className="text-sm">
                                    You're about to grant superadmin
                                </AlertTitle>
                                <AlertDescription className="text-xs">
                                    Superadmins can do nearly anything in
                                    Tagada — only the founder gate stops them
                                    from minting more superadmins. Grant only
                                    to people you would trust with production
                                    root.
                                </AlertDescription>
                            </Alert>
                        )}
                        {wantsFounder && (
                            <Alert
                                variant="destructive"
                                className="mt-3 border-rose-200 bg-rose-50 text-rose-900"
                            >
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle className="text-sm">
                                    You're about to grant founder
                                </AlertTitle>
                                <AlertDescription className="text-xs">
                                    Founder is the root-of-trust role — it is
                                    the only role that can grant other
                                    superadmins or other founders. Grant only
                                    to a co-founder-level operator.
                                </AlertDescription>
                            </Alert>
                        )}
                        {!canMutate && (
                            <p className="text-xs leading-relaxed text-muted-foreground">
                                Read-only view. {user.whitelabelOwnership
                                    ? `Ask a superadmin, payment_ops, or the ${user.whitelabelOwnership} partneradmin to change these.`
                                    : 'Internal-staff roles can only be changed by a superadmin.'}
                            </p>
                        )}
                    </section>
                </div>

                {/*
                  * Persistent error banner. Sits above the footer so it
                  * is the FIRST thing the operator sees when scanning
                  * back from a failed Save. Cleared on next attempt
                  * (`onSave`/`onRevoke`) or when the sheet re-opens for
                  * a different user.
                  */}
                {lastError && (
                    <div className="border-t px-6 py-4">
                        <IamErrorAlert error={lastError} />
                    </div>
                )}

                <SheetFooter className="flex-col gap-2 border-t bg-muted/30 px-6 py-4 sm:flex-row sm:justify-between">
                    {canMutate && user.whitelabelOwnership ? (
                        <Button
                            variant="ghost"
                            onClick={onRevoke}
                            disabled={isSaving}
                            className="text-rose-700 hover:bg-rose-50 hover:text-rose-800 sm:mr-auto"
                        >
                            {revokeTeam.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <UserMinus className="mr-2 h-4 w-4" />
                            )}
                            Revoke from {user.whitelabelOwnership}
                        </Button>
                    ) : (
                        <span />
                    )}
                    <div className="flex gap-2">
                        <Button
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={isSaving}
                        >
                            {canMutate ? 'Cancel' : 'Close'}
                        </Button>
                        {canMutate ? (
                            <Button
                                onClick={onSave}
                                disabled={isSaving || (!rolesChanged && !scopeChanged)}
                            >
                                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Save changes
                            </Button>
                        ) : null}
                    </div>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}

/**
 * Invite a teammate by email from the unified IAM directory.
 *
 * Routes to the right backend mutation based on the chosen scope so
 * the operator never has to remember the difference:
 *   - Partner scope → `inviteTeamMember` use case. Handles "email
 *     not yet in Clerk" (Clerk invitation), "email already in this
 *     partner" (merge roles), and rejects "email belongs to a
 *     different partner / internal staff / merchant user" (cross-
 *     partner hijack guard).
 *   - Internal scope → `findUserByEmail` + `setUserRoles`. Internal
 *     staff cannot be invited from the CRM today: if the email has
 *     no Clerk account, the operator is asked to create the user
 *     in the Clerk Dashboard first (this preserves the legacy
 *     /admin/staff "promote by email" semantics and avoids minting
 *     unscoped Clerk invitations from the CRM).
 *
 * Restricting invites to scopes the backend will actually accept
 * (PARTNER_ASSIGNABLE_ROLES vs INTERNAL_ASSIGNABLE_ROLES) keeps the
 * dialog symmetric with the edit sheet.
 */
function InviteDialog({
    open,
    onOpenChange,
    partners,
    internalAssignableRoles,
    partnerAssignableRoles,
    onInvited,
    isSuperAdmin,
    isFounder,
    callerPartnerScope,
}: {
    open: boolean;
    onOpenChange: (o: boolean) => void;
    partners: readonly { slug: string; name: string }[];
    internalAssignableRoles: readonly string[];
    partnerAssignableRoles: readonly string[];
    onInvited: () => void;
    /** Drives whether the "Internal Tagada" scope is offered. */
    isSuperAdmin: boolean;
    /**
     * Caller carries the founder tgdRole. Same gating rule as in the
     * UserEditSheet: only a founder can grant `superadmin` or
     * `founder` (the backend `canAssignTgdRole` enforces this; the
     * dialog disables the checkboxes to avoid 403-trap clicks).
     */
    isFounder: boolean;
    /**
     * Caller's own partner scope (null for internal staff).
     * Non-superadmins can only invite into:
     *   - their own partner scope (partneradmins)
     *   - any partner scope (payment_ops / tagadapaymentAdmin — they
     *     all inherit `canManagePartnerStaff` for every partner)
     * The dialog narrows the scope dropdown accordingly so we don't
     * surface options the backend would reject anyway.
     */
    callerPartnerScope: string | null;
}) {
    // Scope options — narrowed based on caller's privileges so the
    // dropdown never offers a path the backend will reject. The
    // `partners` list is already filtered to active partners.
    const scopeOptions = React.useMemo(() => {
        if (isSuperAdmin) {
            return [
                { slug: SCOPE_INTERNAL, name: 'Internal Tagada' },
                ...partners.map((p) => ({ slug: p.slug, name: p.name })),
            ];
        }
        // Non-superadmin: hide internal-staff path (they can't grant
        // internal roles anyway). Partner staff invitations use the
        // policy-checked use-case.
        if (callerPartnerScope) {
            // partneradmin scoped to one partner — only their own.
            const own = partners.find((p) => p.slug === callerPartnerScope);
            return own
                ? [{ slug: own.slug, name: own.name }]
                : [{ slug: callerPartnerScope, name: callerPartnerScope }];
        }
        // payment_ops / tagadapaymentAdmin — any partner staff scope.
        return partners.map((p) => ({ slug: p.slug, name: p.name }));
    }, [isSuperAdmin, callerPartnerScope, partners]);

    const defaultScope = scopeOptions[0]?.slug ?? SCOPE_INTERNAL;

    const [email, setEmail] = React.useState('');
    const [scope, setScope] = React.useState<string>(defaultScope);
    const [draftRoles, setDraftRoles] = React.useState<string[]>([]);
    /**
     * Persistent error banner — same rationale as the UserEditSheet:
     * a 4-second toast for a permission denial leaves the operator
     * staring at an unchanged form wondering what just happened.
     */
    const [lastError, setLastError] = React.useState<IamFriendlyError | null>(null);

    React.useEffect(() => {
        if (!open) {
            setEmail('');
            setScope(defaultScope);
            setDraftRoles([]);
            setLastError(null);
        }
    }, [open, defaultScope]);

    React.useEffect(() => {
        // Reset role selection when scope flips because the assignable
        // sets are disjoint and the previous picks may now be invalid.
        setDraftRoles([]);
        setLastError(null);
    }, [scope]);

    const utils = trpc.useUtils();
    const inviteTeam = trpc.partners.inviteTeamMember.useMutation();
    const setRolesInternal = trpc.partners.setUserRoles.useMutation();
    const lookupQuery = trpc.partners.findUserByEmail.useQuery(
        { email },
        {
            enabled: open && scope === SCOPE_INTERNAL && /.+@.+\..+/.test(email),
            staleTime: 0,
            retry: 0,
        },
    );

    const isInternal = scope === SCOPE_INTERNAL;
    const assignableRoles = isInternal
        ? internalAssignableRoles
        : partnerAssignableRoles;

    const isSubmitting = inviteTeam.isPending || setRolesInternal.isPending;
    const internalLookupExists = !!lookupQuery.data?.clerkUserId;
    const canSubmit =
        /.+@.+\..+/.test(email) &&
        draftRoles.length > 0 &&
        !isSubmitting &&
        (isInternal ? internalLookupExists : true);

    const toggleRole = (r: string) => {
        if (FOUNDER_ONLY_GRANTABLE_ROLES.has(r) && !isFounder) {
            // Synthesise an IAM-shaped error so the dialog uses the
            // exact same friendly copy the backend would have surfaced
            // had the gate fired server-side. Keeps the messaging
            // single-sourced through `toIamFriendlyError`.
            setLastError(
                toIamFriendlyError({
                    name: 'IamAuthorizationError',
                    reason: 'only_founder_can_grant_privileged_role',
                    message: `Roles ['${r}'] can only be granted by a founder.`,
                } as unknown),
            );
            return;
        }
        setLastError(null);
        setDraftRoles((prev) =>
            prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r],
        );
    };

    const onSubmit = async () => {
        try {
            setLastError(null);
            if (isInternal) {
                if (!lookupQuery.data?.clerkUserId) {
                    setLastError({
                        title: 'No Clerk account for this email',
                        description:
                            'Internal-staff invitations require an existing Clerk user. We don\u2019t mint Clerk accounts from the CRM (would bypass org provisioning).',
                        suggestion:
                            'Create the user in the Clerk Dashboard first, then come back here to grant their tgdRoles.',
                        variant: 'warning',
                    });
                    return;
                }
                await setRolesInternal.mutateAsync({
                    clerkUserId: lookupQuery.data.clerkUserId,
                    roles: draftRoles,
                });
                toast.success(`Granted ${draftRoles.join(', ')} to ${email}`);
            } else {
                const res = await inviteTeam.mutateAsync({
                    partner: scope,
                    email,
                    roles: draftRoles,
                });
                toast.success(
                    res?.status === 'invited'
                        ? `Invitation sent to ${email}`
                        : `Updated ${email} (${draftRoles.join(', ')})`,
                );
            }
            await utils.partners.listIamUsers.invalidate();
            onInvited();
            onOpenChange(false);
        } catch (err) {
            setLastError(toIamFriendlyError(err));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Invite a user</DialogTitle>
                    <DialogDescription>
                        Internal staff path needs an existing Clerk account.
                        Partner path can invite a brand-new email.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    <div className="space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Email
                        </Label>
                        <Input
                            type="email"
                            placeholder="teammate@company.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="h-10"
                        />
                        {isInternal &&
                            /.+@.+\..+/.test(email) &&
                            !lookupQuery.isLoading &&
                            !internalLookupExists && (
                                <p className="text-xs text-amber-700">
                                    No Clerk account with this email. Create the
                                    user in Clerk Dashboard first.
                                </p>
                            )}
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Scope
                        </Label>
                        <Select
                            value={scope}
                            onValueChange={setScope}
                            disabled={scopeOptions.length <= 1}
                        >
                            <SelectTrigger className="h-10">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {scopeOptions.map((p) => (
                                    <SelectItem key={p.slug} value={p.slug}>
                                        {p.name}
                                        {p.slug !== SCOPE_INTERNAL && (
                                            <span className="text-muted-foreground">
                                                {' '}
                                                ({p.slug})
                                            </span>
                                        )}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {!isSuperAdmin && (
                            <p className="text-xs text-muted-foreground">
                                Internal Tagada scope is reserved for
                                superadmins. You can invite into{' '}
                                {callerPartnerScope ? 'your partner' : 'any partner'}.
                            </p>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <div className="flex items-baseline justify-between">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Roles
                            </Label>
                            <span className="text-[10px] text-muted-foreground/70">
                                {isInternal
                                    ? 'internal-assignable'
                                    : `partner-assignable · ${scope}`}
                            </span>
                        </div>
                        {/* Caller-capability one-liner. Same rationale
                          * as the UserEditSheet: the operator should
                          * see *immediately* which roles they can grant
                          * and which are locked, instead of having to
                          * tick boxes to find out. */}
                        <div className="flex items-start gap-2 rounded-md border border-sky-200/60 bg-sky-50/60 px-3 py-2 text-xs leading-relaxed text-sky-900">
                            {isFounder ? (
                                <Crown className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-rose-600" />
                            ) : (
                                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-sky-700" />
                            )}
                            <div className="min-w-0 flex-1 font-medium">
                                You can grant{' '}
                                {assignableRoles
                                    .filter(
                                        (r) =>
                                            isFounder ||
                                            !FOUNDER_ONLY_GRANTABLE_ROLES.has(r),
                                    )
                                    .join(', ') || 'no role here'}
                                {!isFounder &&
                                    assignableRoles.some((r) =>
                                        FOUNDER_ONLY_GRANTABLE_ROLES.has(r),
                                    ) && (
                                        <span className="ml-1 font-normal text-sky-800/70">
                                            ·{' '}
                                            <Lock className="mr-0.5 inline h-3 w-3" />
                                            founder-only:{' '}
                                            {assignableRoles
                                                .filter((r) =>
                                                    FOUNDER_ONLY_GRANTABLE_ROLES.has(
                                                        r,
                                                    ),
                                                )
                                                .join(', ')}
                                        </span>
                                    )}
                            </div>
                        </div>
                        <div className="divide-y rounded-md border">
                            {assignableRoles.map((r) => {
                                const checked = draftRoles.includes(r);
                                const isFounderOnly =
                                    FOUNDER_ONLY_GRANTABLE_ROLES.has(r);
                                const isFounderLockedForGrant =
                                    isFounderOnly && !isFounder;
                                return (
                                    <label
                                        key={r}
                                        htmlFor={`invite-role-${r}`}
                                        className={cn(
                                            'flex items-center gap-3 px-3 py-2.5 transition-colors',
                                            isFounderLockedForGrant
                                                ? 'cursor-default bg-muted/20 opacity-70'
                                                : 'cursor-pointer',
                                            checked
                                                ? 'bg-muted/50'
                                                : !isFounderLockedForGrant
                                                    ? 'hover:bg-muted/30'
                                                    : '',
                                        )}
                                        title={
                                            isFounderLockedForGrant
                                                ? 'Only a founder can grant this role.'
                                                : undefined
                                        }
                                    >
                                        <Checkbox
                                            id={`invite-role-${r}`}
                                            checked={checked}
                                            onCheckedChange={() => toggleRole(r)}
                                            disabled={isFounderLockedForGrant}
                                        />
                                        <RoleChip role={r} className="text-[12px]" />
                                        {isFounderOnly && (
                                            <span
                                                className={cn(
                                                    'ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider',
                                                    isFounder
                                                        ? 'text-rose-600'
                                                        : 'text-muted-foreground',
                                                )}
                                            >
                                                {isFounder ? (
                                                    <>
                                                        <Crown className="h-3 w-3" />
                                                        founder grant
                                                    </>
                                                ) : (
                                                    <>
                                                        <Lock className="h-3 w-3" />
                                                        founder only
                                                    </>
                                                )}
                                            </span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                        {draftRoles.includes('superadmin') && (
                            <Alert
                                variant="destructive"
                                className="border-rose-200 bg-rose-50 text-rose-900"
                            >
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle className="text-sm">
                                    Granting superadmin
                                </AlertTitle>
                                <AlertDescription className="text-xs">
                                    Full production root access. Grant
                                    deliberately.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>

                    {lastError && <IamErrorAlert error={lastError} />}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button onClick={onSubmit} disabled={!canSubmit}>
                        {isSubmitting && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {isInternal ? 'Grant role' : 'Send invite'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
